/**
 * Post-consent landing gate (issue #23952). Sits AFTER
 * `resolveAuthGateScreen()` returns `'app'` — so every branch below already
 * has an authenticated session with resolved employer facts (or a
 * `sessionKind === 'employee'` session that uses boot facts). Decides
 * whether to render the dashboard (`children`), the first-run setup screen,
 * the payroll-inactive screen, or a "no payroll role" fallback.
 *
 * {@link resolveConnectLanding} (`lib/auth/connect_landing_logic.ts`) is the
 * pure source of truth for the decision; this component is the thin React
 * glue that fetches organisation status and renders each branch.
 *
 * The organisation-status query is skipped for employees, no-role users, and
 * the native shell's employee-only runtime via
 * {@link shouldQueryOrganisationStatus} + `useOrganisationStatus`'s
 * `enabled` flag — they never see the setup screen, so paying for the round
 * trip is wasted (and the query is admin-scoped in practice).
 */
import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, EmptyState, toast } from '@jumpstone/ui-react';
import { useAuth } from '../../lib/auth/auth_context.js';
import { useSession } from '../../data/use_session.js';
import { BootGateShell } from './session_boot_states.js';
import type { OAuthEntityContext } from '../../lib/auth/oauth_entity_context.js';
import type { SessionFacts } from '../../data/wagepro_store_session_facts.js';
import { useOrganisationStatus } from '../../data/use_organisation_status.js';
import {
  deriveIsAdmin,
  resolveConnectLanding,
  resolvePostConnectToast,
  shouldQueryOrganisationStatus,
} from '../../lib/auth/connect_landing_logic.js';
import { FirstRunSetupScreen } from '../../screens/setup/first_run_setup_screen.js';
import { ledgerEntityDashboardUrl } from '../../lib/auth/ledger_web_url.js';

/**
 * A `payroll_role === 'none'` firm member whose Ledger role does not include
 * payroll. Replaces the old bare-dead-end `NoPayrollAccessScreen` copy —
 * still boot-gate-shell layout, but with a "Back to Ledger" primary action
 * and Sign out as a secondary escape.
 */
function NoPayrollRoleScreen({
  entityContext,
  companyName,
  signOut,
}: {
  entityContext: Extract<OAuthEntityContext, { status: 'ready' }>;
  companyName: string;
  signOut: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const backHref = ledgerEntityDashboardUrl(entityContext.entity_id);
  return (
    <BootGateShell>
      <EmptyState
        title={t('setup.no_payroll_role.title', { org: companyName })}
        description={t('setup.no_payroll_role.description')}
        action={
          <div className="flex flex-col items-center gap-2">
            <Button
              variant="primary"
              onClick={() => {
                window.location.href = backHref;
              }}
            >
              {t('setup.actions.back_to_ledger')}
            </Button>
            <Button variant="ghost" onClick={() => void signOut()}>
              {t('account.sign_out')}
            </Button>
          </div>
        }
      />
    </BootGateShell>
  );
}

/**
 * Loading placeholder while the organisation-status query is in flight —
 * copy matches the `main.tsx` `BOOT_PLACEHOLDER` update: "Loading your
 * WagePro account…" (`boot.loading_account`).
 */
function ConnectingPlaceholder(): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <div className="min-h-dvh flex items-center justify-center p-6 text-sm text-[color:var(--ink-muted)]">
      {t('boot.loading_account')}
    </div>
  );
}

/**
 * `connected && !has_payroll_entitlement` — the payroll add-on is revoked,
 * expired, or was never granted. No connect CTA: `connectOrganisation` would
 * refuse to re-grant (the company row already exists), so a button here would
 * spin the same false-success loop the adversarial review of PR #23958 caught
 * (HIGH). Fix path is Ledger-side; escalating to "Back to Ledger" or Sign out
 * is the only real remedy.
 */
function PayrollInactiveScreen({
  entityContext,
  companyName,
  signOut,
}: {
  entityContext: Extract<OAuthEntityContext, { status: 'ready' }>;
  companyName: string;
  signOut: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const backHref = ledgerEntityDashboardUrl(entityContext.entity_id);
  return (
    <BootGateShell>
      <EmptyState
        title={t('setup.payroll_inactive.title', { org: companyName })}
        description={t('setup.payroll_inactive.description')}
        action={
          <div className="flex flex-col items-center gap-2">
            <Button
              variant="primary"
              onClick={() => {
                window.location.href = backHref;
              }}
            >
              {t('setup.actions.back_to_ledger')}
            </Button>
            <Button variant="ghost" onClick={() => void signOut()}>
              {t('account.sign_out')}
            </Button>
          </div>
        }
      />
    </BootGateShell>
  );
}

function OrganisationStatusErrorScreen({
  retry,
}: {
  retry: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <BootGateShell>
      <EmptyState
        title={t('setup.status_error.title')}
        description={t('setup.status_error.description')}
        action={
          <Button variant="secondary" onClick={retry}>
            {t('setup.status_error.retry')}
          </Button>
        }
      />
    </BootGateShell>
  );
}

export interface ConnectLandingGateProps {
  entityContext: Extract<OAuthEntityContext, { status: 'ready' }>;
  facts: SessionFacts;
  /** Rendered when the landing decision is `dashboard` — i.e. the app is open for business. */
  children: React.ReactNode;
}

export function ConnectLandingGate({
  entityContext,
  facts,
  children,
}: ConnectLandingGateProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  const { sessionKind, signOut } = useAuth();
  // The native Capacitor shell shows the employee surface only, so every
  // employer-only landing below must be skipped there (and its status query
  // never fired). Read from the store rather than calling `isNative()` here
  // so this component and `App.tsx`'s own admin/employee branch can never
  // disagree about which surface the runtime is showing.
  const { is_employee_view_only } = useSession();
  const sessionKindForDecision: 'employer' | 'employee' =
    sessionKind === 'employee' ? 'employee' : 'employer';
  // Server-parity admin check — see deriveIsAdmin's docblock for why the
  // pre-fixup payroll_role/is_adviser/manage_users derivation was wrong
  // (flagged every firm staffer including readonly as admin-like).
  const isAdmin = deriveIsAdmin({
    firmRole: facts.firm_role,
    entityRole: facts.entity_role,
  });
  const enabled = shouldQueryOrganisationStatus({
    sessionKind: sessionKindForDecision,
    isAdmin,
    employeeViewOnly: is_employee_view_only,
  });

  const { state: statusState, retry: retryStatus } = useOrganisationStatus({
    enabled,
  });

  // Post-connect toast plumbing (second adversarial-review MEDIUM: the
  // pre-fixup handler fired the toast on `result.created` alone, so a
  // `created:true` mutation followed by a refetch that reported
  // `connected + !has_payroll_entitlement` would flash a false "Connected
  // to Ledger · <Org>" toast and then land on the `payroll_inactive`
  // screen). The gate here tracks a `pendingConnect` state that carries
  // the org name from the mutation up to the effect that observes the
  // refetched status:
  //
  // 1. `FirstRunSetupScreen.onConnected({ orgName })` sets pendingConnect
  //    with `awaitingRefetch: true` AND calls `retryStatus()` — the latter
  //    bumps `useOrganisationStatus`'s reload token, which clears data and
  //    briefly transitions the state to `loading` before the next `ready`.
  // 2. The effect below marks `awaitingRefetch: false` on the first
  //    `loading` observation after the click — we're now waiting on the
  //    FRESH `ready`/`error`, not the stale one from before the mutation.
  // 3. On the next non-loading transition, `resolvePostConnectToast`
  //    decides `fire`/`suppress`/`wait` — see its docblock. `fire` shows
  //    the toast; both `fire` and `suppress` clear the pending state.
  const [pendingConnect, setPendingConnect] = React.useState<{
    orgName: string;
    awaitingRefetch: boolean;
  } | null>(null);

  React.useEffect(() => {
    if (pendingConnect === null) return;
    if (pendingConnect.awaitingRefetch) {
      if (statusState.status === 'loading') {
        setPendingConnect({ ...pendingConnect, awaitingRefetch: false });
      }
      return;
    }
    if (statusState.status === 'no_entity') {
      // Cannot happen in practice — the caller only mounts this gate under
      // a `ready` entity context (see the guard in `main.tsx`'s
      // `ConnectLandingGateBridge`) — but the state type still includes
      // `no_entity` in principle. Treat it as "suppress" so a stray
      // transition here does not strand the pending marker.
      setPendingConnect(null);
      return;
    }
    const decision = resolvePostConnectToast(statusState);
    if (decision.decide === 'wait') return;
    if (decision.decide === 'fire') {
      toast.success(
        t('setup.toast_connected', { org: pendingConnect.orgName }),
      );
    }
    setPendingConnect(null);
  }, [pendingConnect, statusState, t]);

  const decision = resolveConnectLanding({
    sessionKind: sessionKindForDecision,
    payrollRole: facts.payroll_role,
    isAdmin,
    employeeViewOnly: is_employee_view_only,
    // When we did not query status, feed the resolver a synthetic
    // "connected + entitled" so its admin-check branches never fire — the
    // non-admin decisions above (employee/no_payroll_role/dashboard) do
    // not touch this value.
    organisationStatus: enabled
      ? statusState.status === 'ready'
        ? {
            status: 'ready',
            connected: statusState.connected,
            has_payroll_entitlement: statusState.has_payroll_entitlement,
          }
        : statusState.status === 'error'
          ? { status: 'error' }
          : { status: 'loading' }
      : { status: 'ready', connected: true, has_payroll_entitlement: true },
  });

  if (decision === 'dashboard' || decision === 'employee_my_pay') {
    return <>{children}</>;
  }

  if (decision === 'no_payroll_role') {
    return (
      <NoPayrollRoleScreen
        entityContext={entityContext}
        companyName={facts.company_name}
        signOut={signOut}
      />
    );
  }

  if (decision === 'payroll_inactive') {
    return (
      <PayrollInactiveScreen
        entityContext={entityContext}
        companyName={facts.company_name}
        signOut={signOut}
      />
    );
  }

  if (decision === 'loading') {
    return <ConnectingPlaceholder />;
  }

  if (decision === 'error') {
    return <OrganisationStatusErrorScreen retry={retryStatus} />;
  }

  return (
    <FirstRunSetupScreen
      entityContext={entityContext}
      onConnected={({ orgName }) => {
        // Arm the pending toast (evaluated by the effect above once the
        // refetch completes) THEN kick off the refetch. Order matters:
        // `awaitingRefetch: true` prevents the effect from evaluating a
        // stale `ready` state that predates the mutation.
        setPendingConnect({ orgName, awaitingRefetch: true });
        retryStatus();
      }}
    />
  );
}
